import React, { useEffect, useState, ChangeEvent } from 'react';
import {
  disconnectGoogle,
  getGoogleStatus,
  listGoogleCalendars,
  setGoogleCalendar,
  startGoogleOAuth,
  GoogleStatus,
  GoogleCalendar,
} from '../api';
import detailMessage from '../detailMessage';
import { useLang } from '../LangContext';

interface Props {
  token: string;
}

const GoogleCalendarSection = ({ token }: Props) => {
  const { t } = useLang();
  const [status, setStatus] = useState<GoogleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [calendars, setCalendars] = useState<GoogleCalendar[] | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [calendarMessage, setCalendarMessage] = useState('');

  const loadCalendars = async () => {
    setCalendarsLoading(true);
    setError('');
    try {
      const data = await listGoogleCalendars(token);
      setCalendars(data.calendars);
      setSelectedId(data.selected_calendar_id);
    } catch (err) {
      setError(detailMessage(err, t.googleCouldNotLoadCalendars));
    } finally {
      setCalendarsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getGoogleStatus(token);
        if (!cancelled) setStatus(data);
      } catch (err) {
        if (!cancelled) setError(detailMessage(err, t.googleFailedLoadStatus));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t.googleFailedLoadStatus]);

  useEffect(() => {
    if (status?.connected
        && (status.missing_scopes || []).length === 0
        && calendars === null
        && !calendarsLoading) {
      loadCalendars();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.connected]);

  const handleConnect = async () => {
    setError('');
    setBusy(true);
    try {
      const { authorization_url } = await startGoogleOAuth(token);
      window.location.assign(authorization_url);
    } catch (err) {
      setError(detailMessage(err, t.googleCouldNotStartSignIn));
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setError('');
    setBusy(true);
    try {
      await disconnectGoogle(token);
      setStatus((prev) => ({
        ...(prev || { enabled: true }),
        connected: false,
        account_email: null,
        connected_at: null,
        calendar_id: prev?.calendar_id ?? null,
        missing_scopes: prev?.missing_scopes ?? [],
      }));
      setCalendars(null);
      setSelectedId('');
    } catch (err) {
      setError(detailMessage(err, t.googleCouldNotDisconnect));
    } finally {
      setBusy(false);
    }
  };

  const handleCalendarChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (!next || next === selectedId) return;
    setError('');
    setCalendarMessage('');
    setBusy(true);
    try {
      await setGoogleCalendar(token, next);
      setSelectedId(next);
      setCalendarMessage(t.googleCalendarSwitching);
      setTimeout(() => setCalendarMessage(''), 8000);
    } catch (err) {
      setError(detailMessage(err, t.googleCouldNotChangeCalendar));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="google-calendar-section">
        <h4>{t.googleCalendarHeading}</h4>
        <p className="duration-options-hint">{t.googleCalendarLoading}</p>
      </section>
    );
  }

  if (!status?.enabled) {
    return null;
  }

  return (
    <section className="google-calendar-section">
      <h4>{t.googleCalendarHeading}</h4>
      <p className="duration-options-hint">
        {t.googleCalendarDesc}
      </p>

      {error && <div className="error-message"><strong>{t.errorLabel2}</strong> {error}</div>}

      {status.connected ? (
        <>
          <div className="google-calendar-status">
            <div>
              <strong>{t.googleCalendarStatusConnected}</strong>
              {status.account_email && (
                <span className="duration-options-hint" style={{ marginLeft: 8 }}>
                  {status.account_email}
                </span>
              )}
            </div>
            <button
              type="button"
              className="submit-button button-danger"
              onClick={handleDisconnect}
              disabled={busy}
            >
              {busy ? t.googleDisconnecting : t.googleDisconnect}
            </button>
          </div>

          {(status.missing_scopes || []).length > 0 && (
            <div className="error-message" style={{ marginTop: 12 }}>
              <strong>{t.googleMissingScopes}</strong>{' '}
              {t.googleMissingScopesDetail}
            </div>
          )}

          <div className="google-calendar-picker">
            <label htmlFor="google-calendar-select">{t.googleSyncTo}</label>
            {calendarsLoading ? (
              <p className="duration-options-hint">{t.googleLoadingCalendars}</p>
            ) : calendars && calendars.length > 0 ? (
              <select
                id="google-calendar-select"
                value={selectedId}
                onChange={handleCalendarChange}
                disabled={busy}
              >
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.summary}{c.primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <p className="duration-options-hint">{t.googleNoWritableCalendars}</p>
            )}
            {calendarMessage && (
              <div className="success-message" style={{ marginTop: 8 }}>{calendarMessage}</div>
            )}
          </div>
        </>
      ) : (
        <button
          type="button"
          className="submit-button"
          onClick={handleConnect}
          disabled={busy}
        >
          {busy ? t.googleRedirecting : t.googleConnectButton}
        </button>
      )}
    </section>
  );
};

export default GoogleCalendarSection;
